import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';

/**
 * Course thumbnails, loaded one course at a time. Some are base64 images of a few MB each; all of
 * them inside the course list made it ~17 MB, which took longer than the app waits for a response
 * ("Failed to load courses"). So pages list the courses with `?thumbnails=false` - each course then
 * says `hasThumbnail` - and load the thumbnails here: GET /api/v2/lms/admin/courses/:id/thumbnail.
 */
export const LIGHT_COURSE_LIST = { thumbnails: false };

// `${course id}|${updatedAt}` -> { promise, at }. A course saved again gets a new key, so a changed
// thumbnail is loaded afresh. Signed links last an hour, so an entry is reused for half of that.
const cache = new Map();
const REUSE_FOR_MS = 30 * 60 * 1000;
const keyOf = (course) => `${course._id}|${course.updatedAt || ''}`;

// The course's thumbnail ('' when it has none). A course that came with its thumbnail keeps it.
export function loadCourseThumbnail(course) {
  if (!course) return Promise.resolve('');
  if (!course.hasThumbnail || course.thumbnail !== undefined) return Promise.resolve(course.thumbnail || '');
  const key = keyOf(course);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < REUSE_FOR_MS) return hit.promise;
  const promise = api
    .get(`/api/v2/lms/admin/courses/${course._id}/thumbnail`)
    .then((res) => res.data?.data?.thumbnail || '');
  cache.set(key, { promise, at: Date.now() });
  promise.catch(() => cache.delete(key));
  return promise;
}

// The course with its thumbnail filled in, for the Edit form: it shows the thumbnail and saves it.
export async function withCourseThumbnail(course) {
  if (!course?.hasThumbnail || course.thumbnail !== undefined) return course;
  return { ...course, thumbnail: await loadCourseThumbnail(course) };
}

/**
 * `courses` with their thumbnails filled in as each one arrives (the others as they are, until then
 * or if one can't be loaded - pages then show the course's icon, as for a course without one).
 */
export function useCourseThumbnails(courses) {
  const [loaded, setLoaded] = useState({}); // key -> thumbnail

  useEffect(() => {
    let alive = true;
    courses
      .filter((course) => course.hasThumbnail && course.thumbnail === undefined)
      .forEach((course) => {
        const key = keyOf(course);
        loadCourseThumbnail(course)
          .then((thumbnail) => {
            if (alive) setLoaded((prev) => (prev[key] === thumbnail ? prev : { ...prev, [key]: thumbnail }));
          })
          .catch(() => {});
      });
    return () => {
      alive = false;
    };
  }, [courses]);

  return useMemo(
    () => courses.map((course) => (loaded[keyOf(course)] !== undefined ? { ...course, thumbnail: loaded[keyOf(course)] } : course)),
    [courses, loaded]
  );
}

// One course's thumbnail: its own, or loaded when the list left it out ('' until then).
export function useCourseThumbnail(course) {
  const one = useMemo(() => (course ? [course] : []), [course]);
  return useCourseThumbnails(one)[0]?.thumbnail || '';
}
